// Content for the courier route pages (CourierRoutePage.jsx). Prices and
// transit times come from rates.json, so a monthly `node scripts/fetch-rates.js`
// refreshes every figure on these pages; the words around them stay put.
//
// Customs facts are from the official sources linked on each page (checked
// October 2026). Re-check them when you touch a page: these rules change.
import {
  carrierName, cheapest, cheapestExpress, checkedText, checkedYear, country, inr, lowestPerKg, transitOf,
} from '../lib/rates';

const PICKUP =
  'Choose door pickup at checkout — it is priced by your PIN code — or drop the parcel at our office in Mahipalpur, New Delhi, for free.';

const facts = (key) => {
  const c = country(key);
  const one = cheapest(key, 1);
  const half = cheapest(key, 0.5);
  const ten = cheapest(key, 10);
  const express = cheapestExpress(key, 1);
  const perKg = lowestPerKg(key);
  return { c, one, half, ten, express, perKg, where: c.example ? `${c.name} (${c.example})` : c.name };
};

const titleFor = (name, f) => `Courier from India to ${name}: Rates from ${inr(f.perKg.perKg)}/kg (${checkedYear})`;

const quickAnswer = (name, f) =>
  `A 1 kg parcel from India to ${name} costs from ${inr(f.one.price)} with ${carrierName(f.one.carrier)}`
  + `${f.one.transit ? `, delivered in ${f.one.transit}` : ''}. Express with ${f.express.carrier} starts at ${inr(f.express.price)}`
  + `${f.express.transit ? `, delivered in ${f.express.transit}` : ''}. Bigger parcels cost less per kg: 10 kg from ${inr(f.ten.price)}, `
  + `about ${inr(Math.round(f.ten.price / 10))}/kg.`;

const transitLine = (key, carrier) => transitOf(key, carrier) || 'no guaranteed delivery date';

const costAnswer = (name, f) =>
  `When we checked on ${checkedText}, sending 0.5 kg to ${name} cost from ${inr(f.half.price)} and 1 kg from ${inr(f.one.price)} `
  + `(${carrierName(f.one.carrier)}), with express from ${inr(f.express.price)} (${f.express.carrier}). The price depends on the `
  + 'chargeable weight — the higher of the actual and volumetric weight — and the service you choose. '
  + 'Use the calculator for today’s price for your parcel.';

// ----------------------------------------------------------------- UK
const uk = (() => {
  const key = 'uk';
  const f = facts(key);
  const name = 'the UK';
  return {
    key,
    path: '/send-courier-from-india-to-uk/',
    flag: { src: '/assets/flags/4x3/gb.svg', alt: 'Flag of the United Kingdom' },
    title: titleFor('UK', f),
    desc: `Courier from India to the UK: 1 kg from ${inr(f.one.price)}, express from ${inr(f.express.price)}. `
      + `Compare DHL, FedEx, UPS, India Post and duty-paid rates, plus UK VAT rules.`,
    h1: 'Courier from India to the UK',
    sub: `Rates from ${inr(f.perKg.perKg)}/kg · express in ${transitLine(key, f.express.carrier)} · duty-paid option`,
    quick: quickAnswer(name, f),
    intro: [
      'Sending a parcel from India to the UK? You can choose between express couriers, our own duty-paid SendMyParcel service, '
        + 'UPS and India Post — and the price for the same parcel can differ by more than half between them. Below are '
        + 'prices by weight for each service, what UK customs charges on arrival, and answers to the questions we are asked most.',
      'The UK is one of the busiest routes out of India: families sending gifts and home comforts to students and relatives, '
        + 'professionals moving their belongings, and small businesses shipping textiles, handicrafts and samples to UK customers.',
    ],
    sections: [
      {
        h2: 'Which service should you choose?',
        list: [
          `Express (DHL, FedEx): ${transitLine(key, 'DHL')} to most UK addresses. Best for documents, urgent gifts and anything time-sensitive.`,
          `SendMyParcel DDP: our own UK service, ${transitLine(key, 'SendMyParcel DDP')} and usually the lowest price. It is a delivered-duty-paid service, so import charges are handled by the shipper rather than collected from the recipient at the door — ask us how duty is charged for your parcel before you book.`,
          `UPS: ${transitLine(key, 'UPS')}, usually priced between the express couriers and the economy options.`,
          'India Post: Speed Post (EMS) for lighter parcels and Air Parcel for heavier ones. Cheaper than the express couriers, but with no guaranteed delivery date.',
        ],
      },
      {
        h2: 'UK customs duty and VAT on parcels from India',
        paras: [
          'The UK charges import VAT and, above certain values, customs duty on parcels arriving from outside the UK. Under current GOV.UK guidance:',
        ],
        list: [
          'Gifts worth £39 or less are not charged VAT.',
          'Gifts worth £135 or less are not charged customs duty, unless they are excise goods such as alcohol or tobacco.',
          'For goods bought from abroad worth £135 or less, the seller usually includes VAT in the price at checkout.',
          'Above £135, the recipient pays VAT and any duty to the delivery company, which normally holds the parcel until it is paid.',
        ],
        after: [
          'A gift has to be a genuine gift from one person to another — a business sending goods to a customer is not sending a gift, whatever the label says. Declare the real value: under-declaring to stay below a threshold risks the parcel being held, and it is the recipient who has to sort it out.',
        ],
        link: { href: 'https://www.gov.uk/goods-sent-from-abroad/tax-and-duty', label: 'GOV.UK: tax and duty on goods sent from abroad' },
      },
      {
        h2: 'What you can send to the UK',
        list: [
          'Personal gifts, clothing and household items',
          'Sarees, textiles, handicrafts and jewellery (declared at their real value)',
          'Books, documents and educational material',
          'Sealed, commercially packaged dry food such as spices, snacks and sweets — subject to UK food rules',
          'Ayurvedic products and cosmetics that meet UK regulations',
          'Commercial samples and goods sold to UK customers',
        ],
      },
      {
        h2: 'Items that are restricted or not allowed',
        list: [
          'Meat, milk and dairy products — not allowed into Great Britain from India as personal imports',
          'Fresh fruit, vegetables, plants and seeds — restricted, and some need a plant health certificate',
          'Lithium batteries, perfumes, aerosols and other dangerous goods — restricted by airlines and couriers',
          'Cash, precious metals, counterfeit goods, weapons and drugs — prohibited',
        ],
        links: [{ href: '/blog/items-you-cannot-send-internationally/', label: 'Items you cannot send internationally' }],
      },
      {
        h2: 'How it works',
        steps: [
          ['Get a price', 'Enter the weight, size and UK destination to compare every service on the route.'],
          ['Book online', 'Pick a service, add sender and recipient details, and pay online.'],
          ['Pickup or drop-off', PICKUP],
          ['Paperwork', 'Our team helps with the customs declaration and labels.'],
          ['Track to the door', 'Follow the parcel from India to its UK address.'],
        ],
      },
      {
        h2: 'Tips for a cheaper, smoother UK shipment',
        list: [
          'Use a box that fits the contents: couriers charge on volumetric weight when a parcel is large but light.',
          'Compare services for your exact weight — the cheapest option at 1 kg is not always the cheapest at 10 kg.',
          'Write a clear, itemised description and the real value on the customs form; mark genuine gifts as gifts.',
          'Keep the invoice or receipt for anything valuable in case UK customs asks for it.',
        ],
        links: [
          { href: '/blog/send-parcel-india-to-uk-costs-customs-2026/', label: 'Sending a parcel from India to the UK: costs and customs' },
          { href: '/blog/best-packaging-practices-international-ecommerce/', label: 'How to pack an international parcel' },
        ],
      },
    ],
    faq: [
      { q: 'How much does it cost to send a parcel from India to the UK?', a: costAnswer('the UK', f) },
      { q: 'How long does a courier from India to the UK take?', a: `DHL and FedEx quote ${transitLine(key, 'DHL')}, SendMyParcel DDP ${transitLine(key, 'SendMyParcel DDP')} and UPS ${transitLine(key, 'UPS')}. India Post does not guarantee a delivery date. Customs clearance can add a day or more.` },
      { q: 'Do I have to pay customs duty on a parcel to the UK?', a: 'The recipient may. Gifts worth £39 or less pay no VAT and gifts worth £135 or less pay no customs duty (excise goods aside). Purchases worth £135 or less usually have VAT added by the seller; above that, VAT and any duty are collected by the delivery company before the parcel is handed over.' },
      { q: 'Can I send a parcel duty paid (DDP) to the UK?', a: 'Yes. SendMyParcel DDP is on our comparison for the UK: import charges are handled by the shipper rather than collected from the recipient on delivery. Ask us how duty is charged for your parcel before you book.' },
      { q: 'What can’t I send to the UK?', a: 'Meat and dairy products, most fresh produce and plants, dangerous goods such as lithium batteries and perfumes, and prohibited items like cash, counterfeit goods and weapons. Check our list of items you cannot send internationally before you pack.' },
      { q: 'Can you collect the parcel from my home?', a: PICKUP },
    ],
    related: ['usa', 'canada', 'australia', 'europe'],
  };
})();

// ----------------------------------------------------------------- USA
const usa = (() => {
  const key = 'usa';
  const f = facts(key);
  const name = 'the USA';
  return {
    key,
    path: '/send-courier-from-india-to-usa/',
    flag: { src: '/assets/flags/4x3/us.svg', alt: 'Flag of the United States' },
    title: titleFor('USA', f),
    desc: `Courier from India to the USA: 1 kg from ${inr(f.one.price)}, express from ${inr(f.express.price)}. `
      + 'Compare DHL, FedEx, UPS and India Post, and what US customs charges now.',
    h1: 'Courier from India to the USA',
    sub: `Rates from ${inr(f.perKg.perKg)}/kg · express in ${transitLine(key, f.express.carrier)} · all 50 states`,
    quick: quickAnswer(name, f),
    zoneNote: 'Prices are for New York. Most services charge the same across the USA; our SendMyParcel service is priced by state, so enter the state in the calculator for an exact price.',
    intro: [
      'The India–USA corridor is one of the highest-volume routes for personal parcels and small-business shipments alike: students and families, professionals relocating, and sellers shipping to US customers on Etsy, Amazon and their own websites. Below are prices by weight for every service we quote, how US customs works since the end of the duty-free allowance, and answers to common questions.',
    ],
    sections: [
      {
        h2: 'Which service should you choose?',
        list: [
          `Express (DHL, FedEx): ${transitLine(key, 'FedEx')} with FedEx and ${transitLine(key, 'DHL')} with DHL. Best for documents, urgent gifts and time-critical business shipments.`,
          `SendMyParcel DDP: our own duty-paid US service, ${transitLine(key, 'SendMyParcel DDP')}, often the lowest price for lighter parcels. Import charges are handled by the shipper rather than collected at the door — ask us how duty is charged for your parcel before you book.`,
          `UPS: ${transitLine(key, 'UPS')}.`,
          'India Post: Speed Post (EMS) for lighter parcels and Air Parcel for heavier ones — competitive on price, with no guaranteed delivery date.',
        ],
      },
      {
        h2: 'US customs duty on parcels from India',
        paras: [
          'Since 29 August 2025 the USA has suspended its $800 duty-free (“de minimis”) allowance for imports from every country, so most parcels from India now face US duty whatever their value. Rules for gifts and for postal shipments differ and are still changing, so check current US Customs and Border Protection (CBP) guidance for your item before you ship.',
          'What has not changed: an accurate, itemised declaration with the real value is the biggest single factor in whether a parcel clears quickly. Business shipments need a commercial invoice; food is regulated by the US Food and Drug Administration, and commercially packaged, labelled food clears more easily than homemade food.',
        ],
        link: { href: 'https://www.cbp.gov/', label: 'US Customs and Border Protection (CBP)' },
      },
      {
        h2: 'What you can send to the USA',
        list: [
          'Documents, gifts, clothing and personal items',
          'Textiles, handicrafts and jewellery (declared at their real value)',
          'Commercially packaged food — subject to FDA rules',
          'E-commerce orders and commercial samples, with a commercial invoice',
        ],
      },
      {
        h2: 'Items that are restricted or not allowed',
        list: [
          'Meat products and fresh fruit, vegetables, plants and seeds — restricted by US agriculture rules',
          'Medicines — restricted; prescription medicines need supporting documents',
          'Lithium batteries, perfumes, aerosols and other dangerous goods — restricted by airlines and couriers',
          'Cash, counterfeit goods, weapons and drugs — prohibited',
        ],
        links: [{ href: '/blog/send-medicines-internationally-from-india/', label: 'Can you send medicines internationally?' }],
      },
      {
        h2: 'How it works',
        steps: [
          ['Get a price', 'Enter the weight, size and US state to compare every service.'],
          ['Book online', 'Choose a service, add the addresses and pay online.'],
          ['Pickup or drop-off', PICKUP],
          ['Paperwork', 'Our team helps with the customs declaration, invoices and labels.'],
          ['Track to the door', 'Follow the parcel all the way to its US address.'],
        ],
        links: [
          { href: '/blog/send-parcel-india-to-usa-costs-customs-2026/', label: 'Sending a parcel from India to the USA: costs, customs and timelines' },
          { href: '/blog/best-international-courier-etsy-sellers-india/', label: 'Best international courier for Etsy sellers in India' },
        ],
      },
    ],
    faq: [
      { q: 'How much does it cost to send a parcel from India to the USA?', a: costAnswer('the USA', f) },
      { q: 'How long does a courier from India to the USA take?', a: `FedEx quotes ${transitLine(key, 'FedEx')}, DHL ${transitLine(key, 'DHL')}, UPS ${transitLine(key, 'UPS')} and SendMyParcel DDP ${transitLine(key, 'SendMyParcel DDP')}. India Post does not guarantee a delivery date. Customs clearance can add time.` },
      { q: 'Is there duty on parcels from India to the USA?', a: 'Usually, yes. The USA suspended its $800 duty-free allowance for all countries from 29 August 2025, so most parcels now attract duty whatever their value. Check CBP guidance for your item before you ship.' },
      { q: 'Can I send a parcel duty paid (DDP) to the USA?', a: 'Yes. SendMyParcel DDP is on our comparison for the USA: import charges are handled by the shipper rather than collected from the recipient. Ask us how duty is charged for your parcel before you book.' },
      { q: 'Can you collect the parcel from my home?', a: PICKUP },
    ],
    related: ['uk', 'canada', 'australia', 'europe'],
  };
})();

// ----------------------------------------------------------------- Canada
const canada = (() => {
  const key = 'canada';
  const f = facts(key);
  const name = 'Canada';
  return {
    key,
    path: '/send-courier-from-india-to-canada/',
    flag: { src: '/assets/flags/4x3/ca.svg', alt: 'Flag of Canada' },
    title: titleFor('Canada', f),
    desc: `Courier from India to Canada: 1 kg from ${inr(f.one.price)}, express from ${inr(f.express.price)}. `
      + 'Compare DHL, FedEx, UPS, India Post and duty-paid rates, plus CBSA rules.',
    h1: 'Courier from India to Canada',
    sub: `Rates from ${inr(f.perKg.perKg)}/kg · express in ${transitLine(key, f.express.carrier)} · duty-paid option`,
    quick: quickAnswer(name, f),
    zoneNote: 'Prices are for Toronto. Our SendMyParcel service is priced by postal code area, so enter the first three characters of the postal code in the calculator for an exact price.',
    intro: [
      'Canada is home to one of the largest Indian communities abroad, and parcels flow both ways all year: gifts and festival packages to family, belongings for students and new arrivals, and goods for Canadian customers. Below are prices by weight for every service we quote, what the Canada Border Services Agency (CBSA) charges, and answers to common questions.',
    ],
    sections: [
      {
        h2: 'Which service should you choose?',
        list: [
          `Express (FedEx, DHL): ${transitLine(key, 'FedEx')} with FedEx and ${transitLine(key, 'DHL')} with DHL.`,
          `SendMyParcel DDP: our own duty-paid Canada service, ${transitLine(key, 'SendMyParcel DDP')}. Import charges are handled by the shipper rather than collected at the door — ask us how duty is charged for your parcel before you book.`,
          `UPS: ${transitLine(key, 'UPS')}.`,
          'India Post Speed Post (EMS): often the lowest price for light parcels, with no guaranteed delivery date.',
        ],
      },
      {
        h2: 'Canadian duty and taxes on parcels from India',
        paras: ['Under CBSA rules for goods arriving by mail or courier:'],
        list: [
          'A gift from a friend or relative abroad worth CAD 60 or less is free of duty and tax; above that, the amount over CAD 60 is assessed. Alcohol, tobacco and items sent by a business do not qualify.',
          'Other goods worth CAD 20 or less arrive free of duty and tax; above that, duty and GST or HST are charged on the full value, plus provincial sales tax in some provinces.',
          'Couriers may add a processing fee when they collect duty and tax from the recipient.',
        ],
        after: ['Include a gift card or note with genuine gifts, and declare the real value — under-declaring is the commonest reason parcels are held.'],
        link: { href: 'https://www.cbsa-asfc.gc.ca/import/courier/menu-eng.html', label: 'CBSA: importing by mail or courier' },
      },
      {
        h2: 'What you can send to Canada',
        list: [
          'Gifts, clothing and personal items',
          'Documents and books',
          'Sealed, commercially packaged dry food — subject to Canadian food rules',
          'Commercial goods and samples, with a commercial invoice',
        ],
      },
      {
        h2: 'Items that are restricted or not allowed',
        list: [
          'Meat, dairy, fresh fruit, plants and seeds — restricted by Canadian food inspection rules',
          'Lithium batteries, perfumes, aerosols and other dangerous goods — restricted by airlines and couriers',
          'Cash, counterfeit goods, weapons and drugs — prohibited',
        ],
        links: [{ href: '/blog/send-food-items-abroad-safely/', label: 'How to send food items abroad safely' }],
      },
      {
        h2: 'How it works',
        steps: [
          ['Get a price', 'Enter the weight, size and postal code to compare every service.'],
          ['Book online', 'Choose a service, add the addresses and pay online.'],
          ['Pickup or drop-off', PICKUP],
          ['Paperwork', 'Our team helps with the customs declaration and labels.'],
          ['Track to the door', 'Follow the parcel all the way to its Canadian address.'],
        ],
      },
    ],
    faq: [
      { q: 'How much does it cost to send a parcel from India to Canada?', a: costAnswer('Canada', f) },
      { q: 'How long does a courier from India to Canada take?', a: `FedEx quotes ${transitLine(key, 'FedEx')}, DHL ${transitLine(key, 'DHL')}, UPS ${transitLine(key, 'UPS')} and SendMyParcel DDP ${transitLine(key, 'SendMyParcel DDP')}. India Post does not guarantee a delivery date.` },
      { q: 'Is there duty on a gift parcel to Canada?', a: 'Not on a gift from a friend or relative worth CAD 60 or less (alcohol and tobacco excepted). Above that, duty and tax are assessed on the amount over CAD 60. Other goods are duty- and tax-free only up to CAD 20.' },
      { q: 'Can I send a parcel duty paid (DDP) to Canada?', a: 'Yes. SendMyParcel DDP is on our comparison for Canada. Ask us how duty is charged for your parcel before you book.' },
      { q: 'Can you collect the parcel from my home?', a: PICKUP },
    ],
    related: ['usa', 'uk', 'australia', 'europe'],
  };
})();

// ----------------------------------------------------------------- Australia
const australia = (() => {
  const key = 'australia';
  const f = facts(key);
  const name = 'Australia';
  return {
    key,
    path: '/send-courier-from-india-to-australia/',
    flag: { src: '/assets/flags/4x3/au.svg', alt: 'Flag of Australia' },
    title: titleFor('Australia', f),
    desc: `Courier from India to Australia: 1 kg from ${inr(f.one.price)}, express from ${inr(f.express.price)}. `
      + 'Compare DHL, FedEx, UPS and India Post, plus biosecurity and GST rules.',
    h1: 'Courier from India to Australia',
    sub: `Rates from ${inr(f.perKg.perKg)}/kg · express in ${transitLine(key, f.express.carrier)} · door pickup in India`,
    quick: quickAnswer(name, f),
    zoneNote: 'Prices are for Sydney. Our SendMyParcel service is priced by postcode, so enter the destination postcode in the calculator for an exact price.',
    intro: [
      'Australia has a large and growing Indian community, from students in Melbourne and Sydney to families across every state. Parcels to Australia need more care than most because of its strict biosecurity rules. Below are prices by weight for every service we quote, what Australian customs checks for, and answers to common questions.',
    ],
    sections: [
      {
        h2: 'Which service should you choose?',
        list: [
          `Express (FedEx, DHL): ${transitLine(key, 'FedEx')} with FedEx and ${transitLine(key, 'DHL')} with DHL.`,
          `SendMyParcel: our own Australia service, ${transitLine(key, 'SendMyParcel')}, often the lowest price for heavier parcels.`,
          `UPS: ${transitLine(key, 'UPS')}.`,
          'India Post Speed Post (EMS): often the lowest price for light parcels, with no guaranteed delivery date.',
        ],
      },
      {
        h2: 'Australian customs, GST and biosecurity',
        list: [
          'Consignments worth AUD 1,000 or less are generally free of duty and GST at the border; GST on goods bought online is usually charged by the seller at checkout. Alcohol and tobacco are taxed at any value.',
          'Above AUD 1,000, the import needs a formal entry and duty, GST and charges are collected before release.',
          'Biosecurity is strict: food, plant material, seeds, wooden items and animal products must be declared, and many are not allowed at all. Undeclared items are a common reason parcels are held or destroyed.',
        ],
        after: ['Describe every item clearly on the customs form — “spices” or “snacks” is not enough; list what they are.'],
        link: { href: 'https://www.abf.gov.au/', label: 'Australian Border Force' },
      },
      {
        h2: 'What you can send to Australia',
        list: [
          'Gifts, clothing and personal items',
          'Documents and books',
          'Commercially packaged, shelf-stable food — declared and subject to biosecurity checks',
          'Commercial goods and samples, with a commercial invoice',
        ],
      },
      {
        h2: 'Items that are restricted or not allowed',
        list: [
          'Fresh food, meat, dairy, eggs, seeds, plants and soil — mostly not allowed',
          'Wooden and cane handicrafts — must be declared and may be inspected',
          'Lithium batteries, perfumes, aerosols and other dangerous goods — restricted by airlines and couriers',
          'Cash, counterfeit goods, weapons and drugs — prohibited',
        ],
        links: [{ href: '/blog/country-specific-shipping-restrictions/', label: 'Country-specific shipping restrictions' }],
      },
      {
        h2: 'How it works',
        steps: [
          ['Get a price', 'Enter the weight, size and postcode to compare every service.'],
          ['Book online', 'Choose a service, add the addresses and pay online.'],
          ['Pickup or drop-off', PICKUP],
          ['Paperwork', 'Our team helps with the customs declaration and labels.'],
          ['Track to the door', 'Follow the parcel all the way to its Australian address.'],
        ],
      },
    ],
    faq: [
      { q: 'How much does it cost to send a parcel from India to Australia?', a: costAnswer('Australia', f) },
      { q: 'How long does a courier from India to Australia take?', a: `FedEx quotes ${transitLine(key, 'FedEx')}, DHL ${transitLine(key, 'DHL')}, UPS ${transitLine(key, 'UPS')} and our SendMyParcel service ${transitLine(key, 'SendMyParcel')}. India Post does not guarantee a delivery date, and biosecurity checks can add time.` },
      { q: 'Can I send food to Australia from India?', a: 'Some commercially packaged, shelf-stable food can be sent if it is declared, but fresh food, meat, dairy, eggs and seeds are mostly not allowed. Undeclared food is a common reason parcels are held or destroyed.' },
      { q: 'Is there duty on parcels to Australia?', a: 'Consignments worth AUD 1,000 or less are generally free of duty and GST at the border (alcohol and tobacco excepted); above that, duty and GST are collected before release.' },
      { q: 'Can you collect the parcel from my home?', a: PICKUP },
    ],
    related: ['uk', 'usa', 'canada', 'europe'],
  };
})();

export const ROUTE_PAGES = { uk, usa, canada, australia };

export const ROUTE_LINKS = {
  uk: { href: '/send-courier-from-india-to-uk/', label: 'India to UK' },
  usa: { href: '/send-courier-from-india-to-usa/', label: 'India to USA' },
  canada: { href: '/send-courier-from-india-to-canada/', label: 'India to Canada' },
  australia: { href: '/send-courier-from-india-to-australia/', label: 'India to Australia' },
  europe: { href: '/send-courier-from-india-to-europe/', label: 'India to Europe' },
};
